import React, { useState } from 'react';
import './HelpCenter.css';

const articles = [
  {
    id: 'getting-started',
    category: 'Getting Started',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    title: 'Welcome to Masters Path',
    summary: 'An overview of the platform, what it does, and how your team uses it.',
    content: `Masters Path is a performance dashboard built for dealerships. It centralizes data entry, tracks KPIs, and surfaces insights across sales, fixed ops, parts, service, and more.

**Your role determines what you can see and do:**
- **Owners** have access to all dealerships and full reporting.
- **Managers** see their dealership's data and can manage their team's data input.
- **Fit Specialists** can be granted access to log daily activity and view their personal performance.

**Getting around:**
Use the sidebar on the left to navigate between sections. Your most-used views will depend on your role—Owners typically start with the Overview, while Fit Specialists will spend most of their time in 10:4 Sales Process Logger.`
  },
  {
    id: 'navigate-dashboard',
    category: 'Getting Started',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
        <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
      </svg>
    ),
    title: 'Navigating the Dashboard',
    summary: 'Learn how the sidebar, views, and filters work together.',
    content: `The sidebar is your primary navigation tool. It's divided into groups:

**Data Entry** — where you log daily activity. Each section corresponds to a department:
- Sales Activity & Daily Logger for sales
- Parts Input, Service Input, Apparel Input for fixed ops and accessories
- Inventory Input for stock tracking
- NPS Input for customer satisfaction scores

**Analytics** — read-only views that show aggregated performance:
- Overview for a high-level snapshot
- Sales & Fixed Ops Performance Dashboards for deep dives
- Scoreboards for competitive team views
- Profit Summary for financial results

**Settings** — manage dealerships, users, and targets (Owner/Manager only)

The active section is highlighted in the sidebar. Click any item to switch views instantly.`
  },
  {
    id: 'daily-logger',
    category: 'Data Entry',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
    title: '10:4 Sales Process',
    summary: 'How to log your daily sales activity quickly and accurately.',
    content: `The Daily Logger is where Fit Specialists daily activities are logged. It's designed to be fast—fill in what happened today and submit.

**What to log:**
- Greets
- Sit Downs
- Test Rides
- Write Ups

**Best practices:**
- Log activity at the end of each day while it's fresh
- Double-check entries before saving
- Use the date picker if you're catching up on a missed day (within the allowed window)

**Saving:** Click **Submit** when done. The entry will appear in the Activity table view. You'll see a green confirmation when the entry is saved successfully.`
  },
  {
    id: 'sales-activity',
    category: 'Data Entry',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
    title: 'F&I Input',
    summary: 'Logging and reviewing team sales submissions.',
    content: `F&I Input sales activity gives Managers and Owners a view of all active and closed sales from their team.

**For F&I Managers:**
- Review daily entries from each rep
- Edit or flag submissions that need correction
- Filter by date range, rep, or department

**For Sales Managers:**
- See your own history of submissions
- Check if a prior entry was flagged or needs updating

**Filters:** Use the date range picker at the top to narrow down to a specific period. You can also filter by individual rep using the dropdown.

**Editing entries:** Click any row to expand it. Managers can edit values directly. Changes are logged with a timestamp and the editor's name.`
  },
  {
    id: 'parts-service-input',
    category: 'Data Entry',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    title: 'Parts, Service & Apparel Input',
    summary: 'Recording parts sales, service revenue, and apparel fixed ops KPIs.',
    content: `These three input screens cover the fixed operations side of your dealership.

**Parts Input:**
- Enter parts sales totals for the day or period
- Track transactions and gross profit

**Service Input:**
- Log repair orders and labor hours
- Record net sales and gross profit
- Track technician productivity if your dealership uses that metric

**Apparel & Licensing Input:**
- Log number of transactions and items sold
- Record net sales and gross profit

**When to enter data:** Most dealerships enter this data daily or weekly depending on how the service manager tracks results. Consistent timing is key for accurate trend analysis.`
  },
  {
    id: 'nps-input',
    category: 'Data Entry',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    ),
    title: 'NPS & Customer Satisfaction',
    summary: 'How to record and track your Net Promoter Score data.',
    content: `The NPS Input screen lets you record customer satisfaction survey results so your scores show up in performance analytics.

**What is NPS?** Net Promoter Score measures customer loyalty on a 0–10 scale. Scores of 9–10 are Promoters, 7–8 are Passives, and 0–6 are Detractors.

**How to enter data:**
1. Select the dealership and date range the surveys cover
2. Enter the total number of surveys received
3. Enter the count of Promoters (9–10), Passives (7–8), and Detractors (0–6)
4. The calculated NPS score will display automatically

**Viewing results:** NPS trends appear on the Overview and Insights dashboards for Owners. Month-over-month changes are highlighted in the KPI cards.`
  },
  {
    id: 'overview-dashboard',
    category: 'Analytics',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: 'Overview Dashboard',
    summary: 'Understanding the KPI cards, alerts, and summary metrics.',
    content: `The Overview dashboard is the first thing you see when you log in (if you're an Owner or Manager). It gives you a snapshot of how each dealership is performing right now.

**KPI Cards:**
Each card shows a metric (e.g., Units Sold, Gross Profit, inventory, and more) alongside its target. Color coding tells you at a glance if you're on track:
- **Green**—at or above target
- **Orange**—slightly below target
- **Red**—significantly below target

**Notifications:** The bell icon in the top corner shows alerts, such as missing daily logs or targets at risk.

**Multi-dealership view:** If you manage multiple rooftops, each one gets its own column so you can compare performance side by side.

**Date range:** The Overview defaults to the current month. Use the period selector to view prior months or YTD figures.`
  },
  {
    id: 'scoreboards',
    category: 'Analytics',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    title: 'Scoreboards',
    summary: 'Using the scoreboard views to motivate your team.',
    content: `Masters Path includes several scoreboard views designed to be displayed in the showroom or sales office.

**Sales Scoreboard**—a ranked list of reps by units sold for the current month. Updates in near real-time as daily logs are submitted.

**Sales Leader Scoreboard**—highlights the top performer with a featured card, useful for recognizing achievement.

**Fixed Ops Scoreboard**—ranks technicians or service advisors by key metrics.

**F&I Scoreboard**—F&I KPIs (reserve, product, total, and penetration) plus a leaderboard ranking F&I managers by frontend, reserve, or average product per motorcycle. At a dealership with a single F&I manager, it shows that person's own stats instead of a ranking. Visible to Owner and General Manager by default; other roles can be granted access under Manage Roles.

**Tips for display use:**
- Open the Scoreboard in a browser and tap the fullscreen button, or press F11 for fullscreen
- The page auto-refreshes so you don't need to reload it manually
- Scoreboards respect your dealership filter—make sure the right rooftop is selected`
  },
  {
    id: 'targets',
    category: 'Settings & Admin',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
      </svg>
    ),
    title: 'Setting Targets',
    summary: 'How to configure monthly and annual targets for each dealership.',
    content: `Targets let you set performance goals that appear as benchmarks on all analytics views.

**Accessing Targets:** Go to **Settings → Targets** in the sidebar. You must be an Owner or Manager with settings access.

**What you can set:**
- Monthly unit sales targets (new and used separately)
- Gross profit targets
- Fixed ops targets (labor revenue, parts sales)
- NPS score goals

**How targets display:**
Once set, targets appear as dotted reference lines on charts and as the denominator in KPI progress calculations. A 75/100 unit month becomes "75% of target."

**Updating targets:** Targets can be updated at any time. Historical entries use the target that was active during that period, so changing the target won't retroactively alter past performance indicators.

**Tip:** Set targets at the start of each month. Many dealerships import annual targets in January and then adjust quarterly.`
  },
  {
    id: 'user-management',
    category: 'Settings & Admin',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Managing Users',
    summary: 'Inviting team members, setting roles, and controlling access.',
    content: `User Management lets Owners and Managers control who has access to Masters Path and what they can do.

**User roles:**
- **Owner**—full access to all dealerships, settings, and analytics
- **Manager**—access to their dealership's data and team management
- **Employee**—data entry access; limited analytics based on permissions

**Inviting a new user:**
1. Go to **Settings → User Management**
2. Click **Invite User**
3. Enter their email and select their role
4. They'll receive an email with a link to set up their account

**Editing permissions:** Click any user's name to open their profile. You can adjust role-specific permissions (e.g., give an Employee access to certain analytics views without promoting them to Manager).

**Deactivating a user:** Toggle the Active switch on their profile. Inactive users cannot log in but their historical data is preserved.`
  },
  {
    id: 'account-preferences',
    category: 'Your Account',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="4"/>
        <path d="M20 21a8 8 0 1 0-16 0"/>
      </svg>
    ),
    title: 'Account Preferences',
    summary: 'Updating your display name, email, and password.',
    content: `Your account preferences let you personalize your profile and keep your credentials secure.

**Accessing your preferences:**
Click your name in the lower-left corner of the sidebar and select **Account Preferences**, or navigate directly to the Account section.

**Display Name:**
This is the name shown in scoreboards, activity logs, and throughout the app. Keep it recognizable to your teammates—first and last name works best.

**Changing your password:**
Your password must meet these requirements:
- At least 12 characters
- At least one uppercase letter
- At least one lowercase letter
- At least one number
- At least one special character (!, @, #, $, etc.)

Click **Change Password**, enter your current password, then your new one twice. Strong passwords help protect your dealership's data.

**Email address:**
Your email is used for login and system notifications. Contact your administrator if you need your email changed.`
  }
];

const categories = [...new Set(articles.map(a => a.category))];

export default function HelpCenter({ user }) {
  const [activeCategory, setActiveCategory] = useState('Getting Started');
  const [activeArticle, setActiveArticle] = useState(articles[0]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredArticles = searchQuery.trim()
    ? articles.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : articles.filter(a => a.category === activeCategory);

  const handleArticleClick = (article) => {
    setActiveArticle(article);
    if (!searchQuery) setActiveCategory(article.category);
  };

  const renderContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h4 key={i} className="help-article-subheading">{line.slice(2, -2)}</h4>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="help-article-li">{renderInline(line.slice(2))}</li>;
      }
      if (/^\d+\./.test(line)) {
        return <li key={i} className="help-article-li help-article-li--numbered">{renderInline(line.replace(/^\d+\.\s*/, ''))}</li>;
      }
      if (line.trim() === '') return <div key={i} className="help-article-spacer" />;
      return <p key={i} className="help-article-p">{renderInline(line)}</p>;
    });
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part
    );
  };

  return (
    <div className="help-center">
      <div className="help-header">
        <div className="help-header-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
            <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none"/>
          </svg>
        </div>
        <div>
          <h1 className="help-title">Help & User Guides</h1>
          <p className="help-subtitle">Everything you need to get the most out of Masters Path</p>
        </div>
      </div>

      <div className="help-search-row">
        <div className="help-search-wrap">
          <svg className="help-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="help-search"
            type="text"
            placeholder="Search guides…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="help-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      </div>

      <div className="help-body">
        <aside className="help-sidebar">
          {!searchQuery && (
            <nav className="help-categories">
              {categories.map(cat => (
                <button
                  key={cat}
                  className={`help-cat-btn${activeCategory === cat ? ' active' : ''}`}
                  onClick={() => {
                    setActiveCategory(cat);
                    const first = articles.find(a => a.category === cat);
                    if (first) setActiveArticle(first);
                  }}
                >
                  {cat}
                </button>
              ))}
            </nav>
          )}

          <ul className="help-article-list">
            {filteredArticles.map(article => (
              <li key={article.id}>
                <button
                  className={`help-article-btn${activeArticle?.id === article.id ? ' active' : ''}`}
                  onClick={() => handleArticleClick(article)}
                >
                  <span className="help-article-btn-icon">{article.icon}</span>
                  <div className="help-article-btn-text">
                    <span className="help-article-btn-title">{article.title}</span>
                    {searchQuery && (
                      <span className="help-article-btn-cat">{article.category}</span>
                    )}
                    <span className="help-article-btn-summary">{article.summary}</span>
                  </div>
                </button>
              </li>
            ))}
            {filteredArticles.length === 0 && (
              <li className="help-no-results">No guides match your search.</li>
            )}
          </ul>
        </aside>

        <article className="help-content">
          {activeArticle && (
            <>
              <div className="help-content-header">
                <span className="help-content-icon">{activeArticle.icon}</span>
                <div>
                  <div className="help-content-category">{activeArticle.category}</div>
                  <h2 className="help-content-title">{activeArticle.title}</h2>
                </div>
              </div>
              <div className="help-content-body">
                {renderContent(activeArticle.content)}
              </div>
            </>
          )}
        </article>
      </div>
    </div>
  );
}
